import "antd/dist/antd.css";
import "../App.css";

import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import CtaCard from "../components/CtaCard";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "motorbike-tool-kit")!;

function MotorbikeToolKitGuide() {
    usePageMeta({
        title:
            "The motorbike tool kit — what to carry, and what to leave | Planamoto",
        description:
            "What actually rides with me — a tiny day-out kit (puncture repair, pump, cable ties, a multi-tool and straps), the bigger roll for long tours, the roadside fixes that work, and why breakdown cover is the most useful tool on the bike.",
        url: "https://www.planamoto.com/guides/motorbike-tool-kit",
    });

    return (
        <GuideArticle
            eyebrow="Guide"
            title="The motorbike tool kit — what to carry, and what to leave"
            lede="You don't need a workshop in your panniers — you need the handful of tools that get you rolling again, and the judgement to leave the rest to recovery. Here's the kit that earns its place, and the kit that just adds weight."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "A motorbike tool roll laid open with tools strapped in rows",
            }}
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            {/* Stack lead + transition live at the top of the children
                rather than in `beforeSections` so they render regardless of
                hero variant — children render in both the photo hero and the
                plain hero, `beforeSections` only in the photo one. */}
            <GuideStackLead
                eyebrow="My roll"
                authorNote="Spencer · what actually rides with me, day out or two weeks"
                heading="What I actually carry"
                intro="Honestly, most of it rarely comes out. The day-out kit is tiny and covers the failure you'll actually meet — a puncture. Longer trips get a bigger roll I reach for a handful of times, often for someone else's bike. For anything genuinely broken, breakdown cover is the most useful tool on the bike."
                columns={[
                    {
                        label: "01 · Day out",
                        title: "Puncture kit, pump, ties, Leatherman, straps",
                        body: "Puncture repair, a pump, a few cable ties, my Leatherman and a small set of straps. That's about as much as I need for a day — and the straps earn their place more than anything else on the list.",
                    },
                    {
                        label: "02 · Longer tours",
                        title: "A bigger roll — used a few times",
                        body: "Add a larger tool set with a few more tools and supplies — tape, fuses, more cable ties. I only reach for it a couple of times a trip, usually a bolt that's worked loose, and as often as not it's to help out a riding buddy.",
                    },
                    {
                        label: "03 · Anything major",
                        title: "Breakdown cover does the heavy lifting",
                        body: "For a real breakdown, the most useful tool is the breakdown-cover card. If a job needs a workbench it needs a van — no roll of spanners in the panniers changes that.",
                    },
                ]}
                backstop="the breakdown-cover card and the recovery number on the same printed sheet as the route. European cover — RAC, ADAC, or your insurer's — turns a trip-ending failure into a long lunch."
            />
            <div className="gt-guide-transition">
                <div className="gt-eyebrow gt-guide-transition__eyebrow">
                    The full kit
                </div>
                <h2 className="gt-guide-transition__heading">
                    That's the roll. Here's the whole list, honestly.
                </h2>
                <p className="gt-guide-transition__intro">
                    Every tool and spare worth considering, grouped by how often it
                    earns its place — with why it's in or out, not a shop's upsell.
                </p>
            </div>

            <GuideSection
                index={1}
                heading="The day-out minimum — what actually rides with me"
                intro="Start here. Five things cover the failure you'll actually meet on a day ride — a puncture — plus the odd jobs a Leatherman and a strap fix. It all tucks under the seat and you'll barely notice it's there."
                photo={{
                    src: "/assets/mc_toolkit/day-out-kit.jpg",
                    alt: "The day-out kit laid out on a table — a Leatherman Wave in a worn leather pouch, a mini pump case, tyre-plug T-handle tools, a puncture repair kit, and a bundle of straps",
                }}
                items={[
                    {
                        title: "Tubeless puncture repair kit",
                        body: "Sticky rope plugs and an insertion tool. The single most useful thing you can carry — a nail through a tubeless tyre becomes a ten-minute roadside fix instead of a recovery truck. Buy a decent one and practise the drill once at home, in the dry, before you ever need it in the rain.",
                    },
                    {
                        title: "A way to put the air back",
                        body: "A compact 12V pump that runs off the battery, or a few CO2 canisters for speed. A plug is useless if you can't reinflate. The pump is slower but never runs out; CO2 is fast but you get two or three shots. I carry the pump and one CO2 for a quick first fill.",
                    },
                    {
                        title: "A few cable ties",
                        body: "The universal roadside repair. A snapped bracket, a dangling fairing panel, a splitting luggage strap — a fistful of zip ties gets you to the next town every single time, and they weigh nothing.",
                    },
                    {
                        title: "A multi-tool — my Leatherman",
                        body: "The one tool that does most day-out jobs: pliers, blades, a screwdriver, a bottle opener for the end of it. Check the bike's own under-seat roll exists and fits your fasteners too — plenty are missing or token — but the Leatherman is what actually comes out.",
                    },
                    {
                        title: "A small set of straps",
                        body: "The most-used item here, and it isn't close. I've strapped a rear mudguard back to the seat after being rear-ended and ridden home, and lashed down an unexpected shopping haul that never would have fit the panniers. A couple of straps weigh nothing and get you out of trouble a spanner never could.",
                    },
                ]}
            />

            <GuideSection
                index={2}
                heading="The longer-tour additions — used rarely, often for someone else"
                intro="On a two-week trip I add a bigger roll. Most of it stays packed the whole time; when it does come out it's usually a bolt that's vibrated loose, and as often as not it's a riding buddy's bike, not mine. Small, cheap, mostly bike-specific — worth the pannier space when you're far from home."
                photo={{
                    src: "/assets/mc_toolkit/longer-tour-kit.jpg",
                    alt: "The longer-tour kit packed up — a GIVI first aid kit, a BMW Motorrad hi-vis vest in its pouch, the rolled-up Kriega tool roll, and a Kriega pouch of spares",
                }}
                items={[
                    {
                        title: "A larger tool set",
                        body: "A compact set of hex and Torx keys plus a few spanners in the sizes your bodywork, levers and screen actually use. Modern bikes are mostly Allen and Torx, not open spanners — walk round once and note what each panel needs. Enough to tighten what's rattled loose, not to rebuild anything.",
                    },
                    {
                        title: "Use the roll for the little jobs at home",
                        body: "Any small job on the bike — fitting an accessory, swapping a lever, nipping up a fairing bolt — do it with the tools from the roll, not the garage bench. If the roll can do it on the driveway it can do it at the roadside, and you'll never discover a missing size 200 miles from home.",
                    },
                    {
                        title: "Tape, fuses and more cable ties",
                        body: "Gaffer tape wound round a pencil, a couple of pounds of assorted blade fuses, and a bigger handful of ties. A blown fuse kills your lights or ignition and looks like a dead bike; the fix is thirty seconds once you know where the box is. Learn the fuse-box location before you go.",
                    },
                    {
                        title: "Clutch and brake levers",
                        body: "A dropped bike snaps a lever, and a bent clutch lever can end a day's riding. Cheap, light and bike-specific — the highest-value spare you can carry. Fit them once before the trip so you know they're the right part and the right fit.",
                    },
                    {
                        title: "Chain lube — for chain-drive bikes",
                        body: "A small can, or a wax stick that won't leak in a hot pannier. A dry chain on a 2,000-mile trip wears fast and runs rough by the second week. Skip it if you ride a shaft or belt drive.",
                    },
                    {
                        title: "A tyre gauge and spare bulbs",
                        body: "A cheap gauge pairs with the plug kit — pressures drift over two weeks, and TPMS won't set a cold pressure with the engine off. If your bike is halogen rather than LED, add a headlight and brake-light bulb; a dead brake light is an instant pull-over in some countries.",
                    },
                    {
                        title: "Nitrile gloves and a rag",
                        body: "Keeps chain oil and road grime off your riding gloves and out of the hotel sink after a roadside fix. A small dignity that makes a big difference to the rest of the day.",
                    },
                ]}
            />

            <GuideSection
                index={3}
                heading="What stays in the garage — and what's a recovery job"
                intro="The bigger roll handles what's worked loose. Everything past that either gets done before you leave or needs a workbench — and on modern roads with phone signal and breakdown cover, that's a call, not a kilo of tools you'll carry for two weeks and never open:"
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={[
                    {
                        title: "Breakdown cover is the real tool",
                        body: "For anything genuinely broken, the most useful thing on the bike is the recovery number. European cover turns a trip-ending failure into a long lunch and a lift to a garage — no roll of spanners does that.",
                    },
                    {
                        title: "Breaker bar and torque wrench",
                        body: "These stay in the garage. The jobs they're for — chain tension, torquing wheel and caliper bolts to spec — get done before you leave, not at a services. Chain tension especially should be set and checked as part of your pre-tour prep, so it's never a roadside problem.",
                    },
                    {
                        title: "Spare tube and tyre levers — on tubeless",
                        body: "Breaking a tubeless tyre off the rim at the roadside is a fantasy without a workshop. A plug kit is the real answer; a genuinely destroyed tyre is a recovery job either way.",
                    },
                    {
                        title: "A chain breaker and spare split link",
                        body: "Worth the weight on a months-long overland trip; dead weight on a two-week European tour, where a snapped chain means a van whether you carry one or not.",
                    },
                    {
                        title: "Anything you've never actually used",
                        body: "If you can't picture the exact roadside moment you'd reach for it, leave it. Weight and faff cost you every day of the trip; the tool costs you once, if ever.",
                    },
                ]}
            />

            <GuideSection
                index={4}
                columns={[
                    {
                        heading: "Tubeless puncture — the roadside fix",
                        items: [
                            {
                                marker: "1",
                                title: "Find it",
                                body: "Look and listen for the nail or the hiss. If you can't see it, a squirt of water from a bottle bubbles at the hole.",
                            },
                            {
                                marker: "2",
                                title: "Ream and plug",
                                body: "Pull the object, ream the hole with the tool, thread a sticky plug onto the inserter and push it in until a few millimetres show. Trim the tail flush.",
                            },
                            {
                                marker: "3",
                                title: "Reinflate and check",
                                body: "Pump back to pressure, wet the plug — no bubbles means it's holding. Ride gently to the next town and get it properly checked or replaced.",
                            },
                        ],
                    },
                    {
                        heading: "Electrical gremlins — before you call anyone",
                        items: [
                            {
                                marker: "✓",
                                title: "Check the obvious first",
                                body: "Kill switch off, sidestand up, fuel tap on, immobiliser fob in range. Half of all 'dead bikes' are one of these four.",
                            },
                            {
                                marker: "✓",
                                title: "Pull the fuse box",
                                body: "A blown fuse is the commonest real fault and the easiest fix. Swap it and see — if it blows again instantly, stop: that's a short, and a recovery job.",
                            },
                            {
                                marker: "✓",
                                title: "Wiggle the battery terminals",
                                body: "Vibration loosens them, and a loose earth mimics a flat battery perfectly. Thirty seconds with the multi-tool can save a recovery call.",
                            },
                        ],
                    },
                ]}
            />

            <GuideCtaBand
                heading="Pack it once. Check it off every trip."
                body="Your tool roll, the spares and the day-before checks live in Planamoto's shared packing checklist — so the plug kit and the breakdown number make it into the panniers before you leave, every trip, for everyone in the group."
            />

            <RelatedGuides
                slugs={[
                    "packing-europe",
                    "bike-security",
                    "motorbike-navigation-tools",
                    "french-toll-roads",
                    "fuel-prices",
                ]}
            />

            <CtaCard
                title="Everything but the spanners — we plan the rest"
                body="Stops, overnight addresses, expenses across currencies and a shared checklist for the kit above — the plan around the ride lives in Planamoto. Browse a curated tour to see a day laid out, or start your own."
                primary={{ label: "Browse tours", to: "/explore" }}
            />
        </GuideArticle>
    );
}

export default MotorbikeToolKitGuide;
